import React from "react";
import { Controller, useFormContext, UseFormWatch } from "react-hook-form";
import { Field, Input, Select } from "./atoms";
import DatePicker from "../DatePicker";
import DateIcon from "../../assets/images/date-icon.png";
import { PhoneNumber } from "../PhoneNumber";

export type FieldType = "text" | "email" | "select" | "phone" | "date";
export type Option = { label: string; value: string | number };

type DynNote =
  | string
  | ((ctx: { watch: UseFormWatch<any> }) => React.ReactNode);

export type FieldConfig = {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  disabled?: boolean;
  options?: Option[];
  placeholder?: string;
  gridSpan?: 1 | 2 | 4;
  min?: string;
  max?: string;
  minFrom?: string;
  suffixText?: DynNote;
  belowNote?: DynNote;
  asyncOptions?: boolean;
  dependsOn?: string;
  /** optional onBlur handler that will run in addition to react-hook-form's onBlur */
  onBlur?: (event?: any) => void;
  numericOnly?: boolean;
};

export const FieldRenderer: React.FC<{ cfg: FieldConfig }> = ({ cfg }) => {
  const {
    control,
    formState: { errors },
    watch,
    clearErrors,
  } = useFormContext();

  const err = (errors as any)?.[cfg.name]?.message as string | undefined;

  const getColClass = (gridSpan?: FieldConfig["gridSpan"]) => {
    switch (gridSpan) {
      case 2:
        return "md:col-span-2";
      case 4:
        return "md:col-span-4";
      default:
        return "";
    }
  };

  const colClass = getColClass(cfg.gridSpan);

  // Select
  if (cfg.type === "select") {
    return (
      <div className={colClass}>
        <Field label={cfg.label} required={cfg.required} error={err}>
          <Controller
            control={control}
            name={cfg.name}
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? ""}
                disabled={cfg.disabled}
                onBlur={(e: any) => {
                  field.onBlur();
                  cfg.onBlur?.(e);
                }}
              >
                {(cfg.options ?? []).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          />
        </Field>
      </div>
    );
  }

  // Phone
  if (cfg.type === "phone") {
    return (
      <div className={colClass}>
        <Field label={cfg.label} required={cfg.required} error={err}>
          <Controller
            control={control}
            name={cfg.name}
            rules={{
              required: cfg.required ? "This field is required." : false,
            }}
            render={({ field }) => (
              <PhoneNumber
                value={field.value || ""}
                defaultCountryCode="us"
                onChange={field.onChange}
                autoFocus={false}
                className="w-full"
              />
            )}
          />
        </Field>
      </div>
    );
  }

  // Date
  if (cfg.type === "date") {
    const dynamicMin = cfg.minFrom ? (watch(cfg.minFrom) as string) : undefined;
    return (
      <div className={colClass}>
        <Controller
          control={control}
          name={cfg.name}
          rules={{ required: cfg.required ? "This field is required." : false }}
          render={({ field, fieldState }) => (
            <DatePicker
              label={cfg.label}
              required={cfg.required}
              error={fieldState.error?.message}
              value={field.value || ""}
              onChange={field.onChange}
              min={dynamicMin || cfg.min || "2000-01-01"}
              max={cfg.max || "2100-12-31"}
              iconSrc={DateIcon}
              highlightToday
            />
          )}
        />
      </div>
    );
  }

  // Text / Email
  return (
    <div className={colClass}>
      <Field label={cfg.label} required={cfg.required} error={err}>
        <Controller
          control={control}
          name={cfg.name}
          render={({ field }) => (
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Input
                  {...field}
                  value={field.value ?? ""}
                  type={cfg.type === "email" ? "email" : "text"}
                  placeholder={cfg.placeholder}
                  disabled={cfg.disabled}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (cfg.numericOnly) value = value.replace(/\D/g, "");
                    field.onChange(value);
                    if (err) clearErrors(cfg.name);
                  }}
                  onBlur={(e: any) => {
                    field.onBlur();
                    cfg.onBlur?.(e);
                  }}
                />
              </div>

              {!!cfg.suffixText && (
                <div className="mt-2 whitespace-nowrap text-[13px] font-semibold text-[#2B2B2B]">
                  {typeof cfg.suffixText === "function"
                    ? cfg.suffixText({ watch })
                    : cfg.suffixText}
                </div>
              )}
            </div>
          )}
        />
      </Field>
    </div>
  );
};
